import { Component, computed, inject } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { MatRadioModule } from '@angular/material/radio';

import { ApiService } from '../../services/api.service';
import { OrderStore } from '../../order-store';
import { OrderStory } from '../../interfaces/order.interface';

@Component({
  selector: 'app-main',
  imports: [DatePipe, MatRadioModule],
  templateUrl: './main.html',
  styleUrl: './main.scss',
})
export class Main {
  private readonly store = inject(OrderStore);
  private readonly router = inject(Router);
  private readonly apiService = inject(ApiService);

  readonly order = this.store.order;
  readonly urlParams = this.store.urlParams;
  readonly confirmedData = this.store.confirmedData;

  // readonly lt = computed(() => this.store.urlParams()?.lt?.toLowerCase() ?? '');

  readonly isOrderPickedUp = computed(() => {
    const routeStatus = this.order()?.routeStatus;
    const routeType = this.urlParams()?.routeType;
    // return status !== 'Order Created' && status !== 'Enroute to Pickup';
    return (
      routeType !== 'pickUp' ||
      (routeType === 'pickUp' && routeStatus === 'COMPLETED')
    );
  });

  readonly allowPreferredDateConfirmation = computed(
    () => this.order()?.allowPreferredDateConfirmation ?? false,
  );

  readonly isEnroute = computed(() => {
    const routeStatus = this.order()?.routeStatus;
    return (
      routeStatus === 'STARTED' ||
      routeStatus === 'ARRIVED' ||
      routeStatus === 'COMPLETED'
    );
  });

  readonly type = computed<'new' | 'edit' | 'none'>(() => {
    const order = this.order();
    if (!order) {
      return 'none';
    }
    const isActive =
      order.status !== 'CANCELLED' &&
      order.status !== 'DELIVERED' &&
      order.routeStatus !== 'COMPLETED' &&
      (order.routeStatus === 'CANCELLED' ||
        order.routeStatus === 'NOT_STARTED') &&
      !this.isEnroute();
    if (!isActive) {
      return 'none';
    }
    return order.confirmedBySMS ? 'edit' : 'new';
  });

  readonly isTrackable = computed(() => {
    const order = this.order();
    if (!order || !order.story.length) {
      return false;
    }
    const status = order.story[order.story.length - 1].status;
    return (
      status === 'Out For Delivery' ||
      status === 'On The Way To Delivery Location' ||
      status === 'Arrived At Delivery Location'
    );
  });

  readonly customStory = computed<OrderStory[]>(() => {
    const order = this.order();
    if (!order) {
      return [];
    }
    const reversed = order.story.length
      ? order.story.slice().reverse()
      : order.story;
    return reversed.filter(
      (level) =>
        level.status !== 'Enroute to Pickup' &&
        level.status !== 'Enroute to Drop off' &&
        level.status !== 'Enroute to Return',
    );
  });

  readonly deliveryDate = computed<Date | null>(() => {
    const order = this.order();
    if (
      !order ||
      !order.story.some((level) => level.status === 'Pickup Completed')
    ) {
      return null;
    }
    const currentDate = new Date();
    const estimatedPlusDays = this.addDays(new Date(order.estimatedTime), 3);
    return currentDate.getTime() >= estimatedPlusDays.getTime()
      ? this.addDays(currentDate, 1)
      : estimatedPlusDays;
  });

  readonly expectedDeliveryDate = computed(() => {
    const time = this.confirmedData()?.time;
    if (!time) {
      return null;
    }
    return this.addDays(new Date(time), 3);
  });

  readonly expectedDeliveryTime = computed(() => {
    const time = this.confirmedData()?.time;
    if (!time) {
      return null;
    }
    const date = new Date(time);
    date.setHours(date.getHours() + 3);
    return date;
  });

  public boxAction(action: string, submit = true): void {
    this.router.navigate([action], {
      queryParamsHandling: 'merge',
      state: { backToMain: 'main', submit: submit },
    });
  }

  public edit(): void {
    if (this.allowPreferredDateConfirmation()) {
      this.router.navigate(['map'], { queryParamsHandling: 'merge' });
    } else {
      this.boxAction('map', true);
    }
    // if (this.lt() === '' && this.isOrderPickedUp()) {
    //   this.router.navigate(['map'], { queryParamsHandling: 'merge' });
    // } else if (this.lt() === 'l' && this.isOrderPickedUp()) {
    //   this.boxAction('time', false);
    // } else {
    //   this.boxAction('map', false);
    // }
  }

  public confirm(): void {
    this.apiService.confirmData();
  }

  public selectAddress(index: number): void {
    const order = this.order();
    if (!order) {
      return;
    }
    const address = order.savedAddresses[index];
    this.store.setConfirmedData({
      location: {
        latitude: Number(address.location.latitude),
        longitude: Number(address.location.longitude),
      },
      address: address.area,
    });
  }

  public liveTrack(): void {
    this.router.navigate(['live-tracking'], { queryParamsHandling: 'merge' });
  }

  private addDays(date: Date, days: number): Date {
    const copy = new Date(date.getTime());
    copy.setDate(copy.getDate() + days);
    return copy;
  }
}
